import { api } from '@api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/solid-query'
import { useService } from 'solid-services'
import { useLessonState } from '../state'

export function useQuizState() {
	const queryClient = useQueryClient()
	const { lessonQuery } = useService(useLessonState)()

	const lessonId = () => lessonQuery.data?.id

	// Fetch Questions and Submissions
	const quizQuery = useQuery(() => ({
		queryKey: ['quiz', lessonId()],
		queryFn: async () => {
			if (!lessonId()) return null

			const { data: authData, error: authErr } = await api().auth.getUser()
			if (authErr || !authData.user) throw new Error('User not authenticated')

			// 1. Fetch Questions
			const { data: questions, error: qErr } = await api()
				.from('course_quiz_questions')
				.select('*')
				.eq('lesson_id', lessonId()!)
				.order('index', { ascending: true })

			if (qErr) throw qErr

			// 2. Fetch Submissions (if questions exist)
			let submissions: any[] = []
			if (questions && questions.length > 0) {
				const questionIds = questions.map((q) => q.id)
				const { data: subs, error: sErr } = await api()
					.from('course_quiz_submissions')
					.select('*')
					.in('question_id', questionIds)
					.eq('user_id', authData.user.id)

				if (sErr) throw sErr
				submissions = subs || []
			}

			return { questions, submissions }
		},
		enabled: !!lessonId(),
		staleTime: 1000 * 60 * 5,
	}))

	// Submit Quiz Mutation (Upserts array of submissions)
	const submitQuizMutation = useMutation(() => ({
		mutationFn: async (
			payload: {
				question_id: string
				selected_indices: number[]
				is_correct: boolean
			}[],
		) => {
			const { data: authData } = await api().auth.getUser()
			if (!authData.user) throw new Error('User not authenticated')

			const upsertData = payload.map((sub) => ({
				user_id: authData.user.id,
				question_id: sub.question_id,
				selected_indices: sub.selected_indices,
				is_correct: sub.is_correct,
				created_at: new Date().toISOString(),
			}))

			const { error } = await api()
				.from('course_quiz_submissions')
				.upsert(upsertData, { onConflict: 'user_id,question_id' })

			if (error) throw error
			return true
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['quiz', lessonId()] })
		},
	}))

	return {
		quizQuery,
		submitQuizMutation,
	}
}
